import { Injectable, afterNextRender, inject } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { MyBetsConfig, MyBetsVisualizationTrackingConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { OpenBetsSummary } from '@frontend/sports/types/models/my-bets';
import { UserService } from '@frontend/sports/user/feature';
import { sum } from 'lodash-es';
import { BehaviorSubject, Subject, filter, firstValueFrom, takeUntil, timer } from 'rxjs';

import { SummaryApiService } from './summary-api.service';

@Injectable({
    providedIn: 'root',
})
export class MyBetsSummaryService {
    private loader = inject(SummaryApiService);
    private dispatcherService = inject(DispatcherService);
    private userService = inject(UserService);
    private mybetsConfig = inject(MyBetsConfig);
    private mybetsVisualizationTrackingConfig = inject(MyBetsVisualizationTrackingConfig);

    private readonly defaultSummary: OpenBetsSummary = {
        openEvents: {},
        liveBetsCount: 0,
        openBetsCount: 0,
        betNumbers: [],
        isMigrationSummary: false,
        hasError: false,
    };

    private readonly EARLY_PAYOUT_SUCCESS = 'EARLY_PAYOUT_SUCCESS';
    private readonly BET_PLACE_SUCCESS = 'BET_PLACE_SUCCESS';
    private readonly CASH_OUT_ALREADY_EXECUTED = 'CASH_OUT_ALREADY_EXECUTED';

    private _summaryUpdate = new BehaviorSubject<Readonly<OpenBetsSummary> | undefined>(undefined);
    readonly summaryUpdate = this._summaryUpdate.pipe(filter(isDefined));

    private stopped$ = new Subject<void>();

    constructor() {
        const productUtils = inject(SportsProductUtils);

        this.stopped$ = new Subject<void>();

        if (this.userService.isAuthenticated) {
            this.start();
        }

        this.userService.onUserLogInWithSettings$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.start());
        this.userService.onUserLogout$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.stop());
        productUtils.fromSportsEnter().subscribe(() => this.userService.isAuthenticated && this.start());
        productUtils.fromSportsLeave().subscribe(() => () => this.stop());

        afterNextRender(() => {
            if (this.userService.isAuthenticated) {
                this.start();
            }
            this.userService.onUserLogInWithSettings$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.start());
            this.userService.onUserLogout$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.stop());
            productUtils.fromSportsEnter().subscribe(() => this.userService.isAuthenticated && this.start());
            productUtils.fromSportsLeave().subscribe(() => () => this.stop());
        });
    }

    get summary(): Readonly<OpenBetsSummary> {
        return this._summaryUpdate.value || this.defaultSummary;
    }

    update(summary: OpenBetsSummary): void {
        this._summaryUpdate.next(summary);
    }

    getOpenBetsCount(evt: EventModel): number {
        const openEvents = this.summary.openEvents;

        if (Object.keys(openEvents).length > 0 && this.mybetsVisualizationTrackingConfig?.isEnabled) {
            return openEvents[evt.id]?.openBetsCount ?? 0;
        }

        const eventIds = [evt.id, ...(evt.splitFixtureIds || []), ...(evt.hybridFixtureData?.fixtureId ? [evt.hybridFixtureData.fixtureId] : [])];
        const eventCounts = eventIds.filter((eid) => openEvents[eid]).map((eid) => openEvents[eid]?.openBetsCount);

        return sum(eventCounts);
    }

    private start(): void {
        this.stop();
        this.subscribeBetPlacementActions();
        firstValueFrom(this.mybetsVisualizationTrackingConfig.whenReady);
        if (!this.mybetsConfig.isOpenSummaryLoadingRestricted) {
            timer(0, this.mybetsConfig.myBetsRefreshInMilliseconds || 120_000)
                .pipe(takeUntil(this.stopped$))
                .subscribe(() => this.refresh());
        }
    }

    async refresh(aggresive: boolean = false): Promise<void> {
        if (aggresive || !this._summaryUpdate.value || this.summary.openBetsCount > 0) {
            let summary: OpenBetsSummary;
            try {
                summary = await this.loader.getOpenBetsCount();
            } catch {
                summary = { ...this.defaultSummary };
            }

            this.update(summary);
        }
    }

    private stop(): void {
        this.stopped$.next();
    }

    private subscribeBetPlacementActions(): void {
        this.dispatcherService.on(this.EARLY_PAYOUT_SUCCESS).pipe(takeUntil(this.stopped$)).subscribe(this.onBetPlacementAction);

        this.dispatcherService.on(this.BET_PLACE_SUCCESS).pipe(takeUntil(this.stopped$)).subscribe(this.onBetPlacementAction);

        this.dispatcherService.on(this.CASH_OUT_ALREADY_EXECUTED).pipe(takeUntil(this.stopped$)).subscribe(this.onBetPlacementAction);
    }

    private onBetPlacementAction = () => {
        this.refresh(true);
    };
}
